import React from 'react';
import { useFeatureFlag } from 'features/feature-set/useFeatureFlag';
import { whiteboardClosed } from 'features/layout/features/content/actions';
import { selectIsLockedContentReadOnly } from 'features/layout/features/content/contentSlice';
import { PermissionTypes } from 'features/permissions/types';
import { usePermissions } from 'features/permissions/usePermissions';
import { useSystemSession } from 'hooks/useSystemSession';
import { GridPanelControl } from 'features/layout/grid-panel/GridPanelControl';
import { selectContentLibraryOpenedFile } from 'features/content-library/contentLibrarySlice';
import { openModal } from 'features/modal/modalSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getDontShowAgainPreference } from 'utils/dontShowAgain';
import { useTranslation } from 'react-i18next';
import { selectWhiteboardIsRestorable } from 'features/whiteboard/drafts/whiteboardDraftsSlice';

export const useWhiteboardCloseControl = () => {
  const dispatch = useAppDispatch();
  const isSystemSession = useSystemSession();

  const { t } = useTranslation('room');

  const openedFile = useAppSelector(selectContentLibraryOpenedFile);

  const { hasPermissions } = usePermissions();

  const canEditWhiteboard = hasPermissions(PermissionTypes.editWhiteboard);
  const isLockedContentReadOnly = useAppSelector(selectIsLockedContentReadOnly);

  const isFeatureEnabled = useFeatureFlag('whiteboard');
  const isContentLibraryEnabled = useFeatureFlag('contentLibrary');

  const controlEnabled = isSystemSession
    ? false
    : isFeatureEnabled && canEditWhiteboard && !isLockedContentReadOnly;

  const canPresentContentLibrary = hasPermissions(PermissionTypes.presentContentLibrary);
  const isContentLibraryFeatureAvailable =
    isContentLibraryEnabled && (canPresentContentLibrary || canEditWhiteboard);

  const whiteboardIsRestorable = useAppSelector(selectWhiteboardIsRestorable);

  const handleClick = () => {
    if (!openedFile?.id) {
      return;
    }

    if (whiteboardIsRestorable) {
      if (getDontShowAgainPreference('closeRestorableWhiteboard')) {
        dispatch(whiteboardClosed(openedFile.id));
      } else {
        dispatch(openModal('closeRestorableWhiteboard'));
      }
      return;
    }

    if (!isContentLibraryFeatureAvailable) {
      dispatch(openModal('closeWhiteboardConfirmation'));
      return;
    }

    dispatch(whiteboardClosed(openedFile.id));
  };

  return {
    controlEnabled,
    handleClick,
    tooltip: t('tooltips.close_content'),
  };
};

export const WhiteboardCloseControl = () => {
  const { controlEnabled, handleClick, tooltip } = useWhiteboardCloseControl();

  return controlEnabled ? (
    <GridPanelControl tooltip={tooltip} icon="close" onClick={handleClick} />
  ) : null;
};
